import { Alert, Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { renderGoogleLoginButton } from './LoginUtils';

/**
 * LoginScreen component - displays a login screen with Google Sign-In button
 * @param {Object} props
 * @param {Function} props.onGoogleLoginSuccess - Callback function called after successful login
 */
export default function LoginScreen({ onGoogleLoginSuccess }) {
    const [showInactivityMessage, setShowInactivityMessage] = useState(false);

    useEffect(() => {
        renderGoogleLoginButton("googleLogin", onGoogleLoginSuccess);

        // Check if user was logged out due to inactivity
        const inactivityLogout = sessionStorage.getItem('inactivityLogout');
        if (inactivityLogout === 'true') {
            setShowInactivityMessage(true);
            // Clear the flag after displaying
            sessionStorage.removeItem('inactivityLogout');
        }
    }, [onGoogleLoginSuccess]);

    return (
        <div style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: '100vh',
            background: 'var(--background-color)',
            color: 'var(--text-primary)'
        }}
        >
            {showInactivityMessage && (
                <Alert
                    severity="info"
                    sx={{
                        mb: 3,
                        maxWidth: '500px',
                        width: '100%',
                        borderRadius: '8px',
                        backgroundColor: 'var(--surface-color)',
                        border: '1px solid var(--border-color)',
                        color: 'var(--text-primary)',
                        '& .MuiAlert-icon': {
                            color: 'var(--text-secondary)'
                        },
                        '& .MuiAlert-message': {
                            color: 'var(--text-primary)',
                            fontSize: '0.875rem',
                            lineHeight: 1.6
                        }
                    }}
                >
                    Session Expired. For your security, you have been logged out due to inactivity.
                </Alert>
            )}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    maxWidth: '440px',
                    px: 2
                }}
            >
                <Typography
                    variant="h5"
                    component="h1"
                    sx={{
                        color: 'var(--text-primary)',
                        fontWeight: 600,
                        mb: 1.5,
                        textAlign: 'center',
                        fontSize: { xs: '1.35rem', sm: '1.5rem' }
                    }}
                >
                    Sign in to your secure workspace
                </Typography>
                <Typography
                    variant="body1"
                    sx={{
                        color: 'var(--text-secondary)',
                        mb: 2.5,
                        textAlign: 'center',
                        fontSize: '0.95rem',
                        lineHeight: 1.6
                    }}
                >
                    Please sign in to ensure secure access to case documents
                </Typography>
                <Typography
                    component="div"
                    sx={{
                        color: 'var(--text-secondary)',
                        fontSize: '0.8rem',
                        textAlign: 'center',
                        lineHeight: 1.6,
                        mb: 2.5
                    }}
                >
                    By clicking below, you agree to our{' '}
                    <Link
                        to="/terms"
                        style={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            textDecoration: 'underline',
                            textUnderlineOffset: '2px'
                        }}
                    >
                        Terms of Service
                    </Link>
                    {' '}and{' '}
                    <Link
                        to="/privacy"
                        style={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            textDecoration: 'underline',
                            textUnderlineOffset: '2px'
                        }}
                    >
                        Privacy Policy
                    </Link>
                    .
                </Typography>
                <Box
                    sx={{
                        cursor: 'pointer',
                        width: '100%',
                        maxWidth: '340px',
                        borderRadius: '14px',
                        padding: '20px 24px',
                        background: 'linear-gradient(145deg, rgba(244, 208, 63, 0.12) 0%, rgba(212, 175, 55, 0.06) 100%)',
                        border: '2px solid var(--accent-color)',
                        boxShadow: '0 6px 20px rgba(212, 175, 55, 0.35), 0 2px 8px rgba(0, 0, 0, 0.06)',
                        overflow: 'hidden',
                        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                            boxShadow: '0 10px 28px rgba(212, 175, 55, 0.45), 0 4px 12px rgba(0, 0, 0, 0.08)',
                            borderColor: 'var(--gradient-primary-start)',
                        }
                    }}
                >
                    <div id="googleLogin" />
                </Box>
            </Box>
        </div>
    );
}

